import { createSelector } from '@reduxjs/toolkit';

import type { AIVoiceFilters } from './ai-voice-modal-slice';

import type { AIVoice } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { RootState } from 'js/src/libs/RootState';

function getAIVoiceModalSliceState(state: RootState) {
    return state.aiVoicesAndAvatars.aiVoiceModal;
}

export function getAIVoiceModalState(state: RootState) {
    return getAIVoiceModalSliceState(state).data.voices;
}

function getAIVoiceModalRequestState(state: RootState) {
    return getAIVoiceModalSliceState(state).request;
}

function getAIVoiceModalFiltersState(state: RootState) {
    return getAIVoiceModalSliceState(state).data.filters;
}

function getAIVoiceModalStoreORM(aiVoicesState: ReturnType<typeof getAIVoiceModalState>) {
    const orm = new PlainJsStoreORM(aiVoicesState);
    return orm;
}

export function getAIVoiceModalVoiceById(state: RootState, id: string): AIVoice | null {
    return getAIVoiceModalStoreORM(getAIVoiceModalState(state)).getById(id) || null;
}

const isDefinedVoice = (v: AIVoice | undefined): v is AIVoice => v !== undefined;

export const getAIVoiceModalVoices = createSelector(
    getAIVoiceModalState,
    (voicesState): AIVoice[] => {
        const orm = getAIVoiceModalStoreORM(voicesState);
        return orm.allIds.map((id) => orm.getById(id)).filter(isDefinedVoice);
    },
);

export const getAIVoiceModalPrivateVoices = createSelector(
    getAIVoiceModalVoices,
    (voices): AIVoice[] => {
        return voices.filter((v) => v.is_private);
    },
);

export const getAIVoiceModalAvailableVoices = createSelector(
    getAIVoiceModalVoices,
    (voices): AIVoice[] => {
        return voices.filter((v) => !v.is_private && !v.is_deprecated);
    },
);

export const getAIVoiceModalDeprecatedVoices = createSelector(
    getAIVoiceModalVoices,
    (voices): AIVoice[] => {
        return voices.filter((v) => !v.is_private && v.is_deprecated);
    },
);

export function getAIVoiceModalFilters(state: RootState): AIVoiceFilters {
    return getAIVoiceModalFiltersState(state);
}

export function getAIVoiceModalRequestIsUninitialized(state: RootState): boolean {
    return getAIVoiceModalRequestState(state).status === 'uninitialized';
}

export function getAIVoiceModalRequestIsPending(state: RootState): boolean {
    return getAIVoiceModalRequestState(state).status === 'pending';
}
